import narcLogo from 'assets/images/logo-narc.png';
import { ConfigurationContext } from 'components/provider/OrginazationContext';
import React, { useContext, useRef, useState } from 'react';
import { BsPeopleFill } from 'react-icons/bs';
import { FaChevronDown, FaChevronUp, FaMapMarkerAlt, FaPhone, FaRedo, FaSearch, FaTimes, FaUsers } from 'react-icons/fa';
import { HiOutlineAcademicCap } from 'react-icons/hi';
import { MdOutlineSchool } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis, YAxis
} from 'recharts';
import { backendURL } from 'redux/reducers/settings';
import {
  TraineeHistoryParams,
  useGetTraineeEthnicitySummary,
  useGetTraineeGenderSummary,
  useGetTraineeTrainingHistory,
  useGetTrainingDetail,
  useGetTrainingList
} from 'service/user.query';

/* ─── constants ──────────────────────────────────── */
const GENDER_COLORS = { male: '#3b82f6', female: '#ec4899', other: '#f59e0b' };
const PIE_COLORS   = ['#3b82f6', '#ec4899', '#f59e0b'];

/* ─── types ──────────────────────────────────────── */
interface TrainingEntry {
  training_name: string;
  batch_name: string;
  training_code: string;
  start_date: string;
  end_date: string;
}

interface TraineeRecord {
  trainee_id: number;
  trainee_name: string;
  namc_or_nhpc: string;
  province: string;
  district: string;
  mobile_number: string;
  email: string;
  total_trainings: number;
  row_total: number;
  trainings: TrainingEntry[];
}

/* ─── helpers ────────────────────────────────────── */
const formatDate = (d: string) =>
  d ? new Date(d).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: '2-digit' }) : '—';

const Shimmer = ({ h = 260 }: { h?: number }) => (
  <div style={{
    height: h, borderRadius: 14,
    background: 'linear-gradient(90deg,#e2e8f0 25%,#f1f5f9 50%,#e2e8f0 75%)',
    backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite'
  }} />
);

/* ─── custom pie label ───────────────────────────── */
const renderPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }: any) => {
  if (percent < 0.04) return null;
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.55;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central"
      style={{ fontSize: 12, fontWeight: 700 }}>
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

/* ─── custom tooltip ─────────────────────────────── */
const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: '#1e293b', borderRadius: 10, padding: '10px 14px',
      boxShadow: '0 8px 24px rgba(0,0,0,0.25)', color: '#f8fafc', fontSize: 13
    }}>
      {label && <p style={{ margin: '0 0 6px', fontWeight: 700, color: '#94a3b8', fontSize: 11, textTransform: 'uppercase' }}>{label}</p>}
      {payload.map((p: any) => (
        <div key={p.name} style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 3 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: p.color, display: 'inline-block' }} />
          <span style={{ color: '#cbd5e1', textTransform: 'capitalize' }}>{p.name}:</span>
          <span style={{ fontWeight: 700 }}>{p.value?.toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
};

/* ─── section header ─────────────────────────────── */
const SectionHeader = ({ icon, title, accent, subtitle }: { icon: React.ReactNode; title: string; accent: string; subtitle?: string }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
    <div style={{
      width: 38, height: 38, borderRadius: 10,
      background: `${accent}18`, display: 'flex', alignItems: 'center',
      justifyContent: 'center', color: accent, fontSize: 17, flexShrink: 0
    }}>
      {icon}
    </div>
    <div>
      <h5 style={{ margin: 0, fontWeight: 700, fontSize: 16, color: '#1e293b' }}>{title}</h5>
      {subtitle && <p style={{ margin: 0, fontSize: 12, color: '#94a3b8' }}>{subtitle}</p>}
    </div>
  </div>
);

/* ─── stat pill ──────────────────────────────────── */
const StatPill = ({ label, value, color }: { label: string; value: number; color: string }) => (
  <div style={{
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    padding: '10px 18px', borderRadius: 12,
    background: `${color}12`, border: `1px solid ${color}30`
  }}>
    <span style={{ fontSize: 22, fontWeight: 800, color, lineHeight: 1 }}>{value?.toLocaleString()}</span>
    <span style={{ fontSize: 11, color: '#64748b', marginTop: 3, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>{label}</span>
  </div>
);

/* ──────────────────────────────────────────────────
   Main component
────────────────────────────────────────────────── */
const TraineeDashboard = () => {
  /* ── trainee history state ── */
  const [filters, setFilters] = useState<TraineeHistoryParams>({
    name: '', namc_or_nhpc: '', mobile_number: '', email: '',
    pageNo: 1, pageSize: 10
  });
  const [searchEnabled, setSearchEnabled]   = useState(false);
  const [expandedTrainee, setExpandedTrainee] = useState<number | null>(null);

  /* ── training list state ── */
  const [trainingQuery, setTrainingQuery]   = useState('');
  const [trainingSearch, setTrainingSearch] = useState('');
  const [trainingPage, setTrainingPage]     = useState(1);
  const TRAINING_PAGE_SIZE = 10;

  /* ── modal state ── */
  const [selectedTrainingId, setSelectedTrainingId] = useState<number | null>(null);
  const [modalOpen, setModalOpen]                   = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  /* ── queries ── */
  const { data: genderData,    isLoading: genderLoading }    = useGetTraineeGenderSummary();
  const { data: ethnicityData, isLoading: ethnicityLoading } = useGetTraineeEthnicitySummary();
  const { data: historyData,   isLoading: historyLoading }   = useGetTraineeTrainingHistory(filters, searchEnabled);
  const { data: trainingListData, isLoading: trainingListLoading } = useGetTrainingList({
    query: trainingSearch, pageNo: trainingPage, pageSize: TRAINING_PAGE_SIZE
  });
  const { data: trainingDetail, isLoading: detailLoading } = useGetTrainingDetail(selectedTrainingId);

  /* ── handlers ── */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };
  const handleSearch = (e: React.FormEvent) => { e.preventDefault(); setSearchEnabled(true); };
  const handleReset  = () => {
    setFilters({ name: '', namc_or_nhpc: '', mobile_number: '', email: '', pageNo: 1, pageSize: 10 });
    setSearchEnabled(false);
    setExpandedTrainee(null);
  };

  const handleTrainingSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setTrainingSearch(trainingQuery);
    setTrainingPage(1);
  };
  const handleTrainingReset = () => {
    setTrainingQuery('');
    setTrainingSearch('');
    setTrainingPage(1);
  };

  const openModal = (id: number) => {
    setSelectedTrainingId(id);
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
    setSelectedTrainingId(null);
  };

  /* ── derived chart data ── */
  const overall      = genderData?.overall;
  const provinceWise: any[]     = genderData?.province_wise || [];
  const ethnicities: any[]      = ethnicityData || [];
  const traineeList: TraineeRecord[] = historyData || [];
  const trainingList: any[]     = trainingListData || [];
  const totalTrainings: number  = trainingList[0]?.row_total ?? trainingList.length;
  const totalPages = Math.ceil(totalTrainings / TRAINING_PAGE_SIZE);

  const overallPieData = overall
    ? [
        { name: 'Male',   value: overall.male },
        { name: 'Female', value: overall.female },
        { name: 'Other',  value: overall.other }
      ].filter(d => d.value > 0)
    : [];

  const provinceBarData = provinceWise.map(p => ({
    province: p.province?.length > 14 ? p.province.slice(0, 13) + '…' : p.province,
    Male: p.male, Female: p.female, Other: p.other
  }));

  const ethnicityBarData = ethnicities.map(e => ({
    ethnicity: e.ethnicity?.length > 16 ? e.ethnicity.slice(0, 15) + '…' : e.ethnicity,
    Male: e.male, Female: e.female, Other: e.other, Total: e.total
  }));

  /* modal province bar data */
  const modalProvinceBar = (trainingDetail?.province_summary || []).map((p: any) => ({
    province: p.province?.replace(' Province', '') || p.province,
    Male: p.male, Female: p.female, Other: p.other
  }));

    const { appConfig } = useContext(ConfigurationContext);
  const navigate = useNavigate();

  return (
    <>
      <style>{`
        @keyframes shimmer { 0%{background-position:-200% 0} 100%{background-position:200% 0} }
        .tr-hover:hover { background: #f0f7ff !important; }
        .expand-row td { animation: fadeIn 0.2s ease; }
        @keyframes fadeIn { from{opacity:0;transform:translateY(-4px)} to{opacity:1;transform:translateY(0)} }
        @keyframes modalIn { from{opacity:0;transform:scale(0.95)} to{opacity:1;transform:scale(1)} }
        .dash-card { background:#fff; border-radius:20px; padding:24px; margin-bottom:20px; box-shadow:0 2px 16px rgba(0,0,0,0.05); }
        .training-card { border-radius:14px; border:1.5px solid #e2e8f0; padding:16px; cursor:pointer; transition:all 0.2s; background:#fff; }
        .training-card:hover { border-color:#3b82f6; box-shadow:0 4px 16px rgba(59,130,246,0.15); transform:translateY(-2px); }
        .modal-overlay { position:fixed; inset:0; background:rgba(15,23,42,0.55); z-index:1000; display:flex; align-items:center; justify-content:center; padding:16px; backdrop-filter:blur(3px); }
        .modal-box { background:#fff; border-radius:20px; width:100%; max-width:820px; max-height:90vh; overflow-y:auto; animation:modalIn 0.22s ease; }
        @media(max-width:767px){
          .gender-chart-grid { flex-direction:column !important; }
          .td-hide-sm { display:none !important; }
          .search-grid { grid-template-columns: 1fr 1fr !important; }
          .training-grid { grid-template-columns: 1fr !important; }
          .modal-box { max-height:95vh; }
          .header-inner { flex-wrap:wrap; justify-content:center; text-align:center; }
          .header-inner .header-logos { display:none !important; }
          .header-center { order:1; }
        }
        @media(max-width:540px){
          .header-inner { gap:12px !important; }
        }
        @media(max-width:420px){
          .search-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      <div style={{ minHeight: '100vh', background: '#f1f5f9', fontFamily: "'Inter', Arial, sans-serif" }}>

        {/* ── Header ── */}
        <div style={{
          background: 'linear-gradient(135deg,#1e40af 0%,#3b82f6 60%,#60a5fa 100%)',
          padding: '22px 24px', position: 'relative', overflow: 'hidden'
        }}>
          {/* decorative circles */}
          {[{ t: -40, r: -40, s: 200 }, { b: -70, l: -30, s: 260 }].map((c, i) => (
            <div key={i} style={{
              position: 'absolute', top: (c as any).t, right: (c as any).r,
              bottom: (c as any).b, left: (c as any).l,
              width: c.s, height: c.s, borderRadius: '50%',
              background: 'rgba(255,255,255,0.05)', pointerEvents: 'none'
            }} />
          ))}

          {/* Login / Register buttons */}
          <div style={{
            position: 'absolute', top: 16, right: 20, zIndex: 2,
            display: 'flex', gap: 10
          }}>
            <button
              onClick={() => navigate('/')}
              style={{
                padding: '7px 18px', borderRadius: 8, cursor: 'pointer',
                background: 'rgba(255,255,255,0.15)',
                border: '1.5px solid rgba(255,255,255,0.5)',
                color: '#fff', fontSize: 13, fontWeight: 600,
                backdropFilter: 'blur(4px)', transition: 'all 0.2s'
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.28)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.15)'; }}
            >
              Login
            </button>
            <button
              onClick={() => navigate('/register')}
              style={{
                padding: '7px 18px', borderRadius: 8, cursor: 'pointer',
                background: '#fff',
                border: '1.5px solid #fff',
                color: '#1e40af', fontSize: 13, fontWeight: 700,
                transition: 'all 0.2s'
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = '#e0f2fe'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = '#fff'; }}
            >
              Register
            </button>
          </div>

          <div style={{ maxWidth: 1200, margin: '0 auto', position: 'relative', zIndex: 1, textAlign: 'center' }}>

            {/* single centred logo */}
            <div style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 88, height: 88, borderRadius: '50%',
              background: 'rgba(255,255,255,0.95)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.25)', padding: 7, marginBottom: 12
            }}>
              <img
                src={appConfig?.logo ? backendURL + appConfig.logo : narcLogo}
                alt={appConfig?.title || 'Logo'}
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            </div>

            <h1 style={{
              margin: '0 0 4px', color: '#fff',
              fontSize: 'clamp(16px,3.5vw,24px)', fontWeight: 800, letterSpacing: 0.3, lineHeight: 1.3
            }}>
              {appConfig?.title || 'Trainee Public Dashboard'}
            </h1>
            <p style={{ margin: '0 0 8px', color: 'rgba(255,255,255,0.7)', fontSize: 13 }}>
              Trainee Public Dashboard
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '5px 18px' }}>
              {appConfig?.address && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: 'rgba(255,255,255,0.88)', fontSize: 12 }}>
                  <FaMapMarkerAlt size={10} /> {appConfig.address}
                </span>
              )}
              {appConfig?.phone_number && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: 'rgba(255,255,255,0.88)', fontSize: 12 }}>
                  <FaPhone size={10} /> {appConfig.phone_number}
                </span>
              )}
            </div>
          </div>
        </div>

        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 16px 40px' }}>

        

          {/* ════════════════════════════════════════
              GENDER SUMMARY
          ════════════════════════════════════════ */}
          <div className="dash-card">
            <SectionHeader
              icon={<FaUsers />}
              title="Gender Summary"
              accent="#3b82f6"
              subtitle="Overall and province-wise gender distribution"
            />

            {genderLoading ? <Shimmer /> : !overall ? (
              <p style={{ color: '#94a3b8', fontSize: 13 }}>No data available.</p>
            ) : (
              <>
                {/* ─ overall stat pills ─ */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 24 }}>
                  <StatPill label="Total"  value={overall.total}  color="#6366f1" />
                  <StatPill label="Male"   value={overall.male}   color={GENDER_COLORS.male} />
                  <StatPill label="Female" value={overall.female} color={GENDER_COLORS.female} />
                  {overall.other > 0 && <StatPill label="Other" value={overall.other} color={GENDER_COLORS.other} />}
                </div>

                {/* ─ charts row ─ */}
                <div className="gender-chart-grid" style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>

                  {/* Donut — overall */}
                  <div style={{ flex: '0 0 280px', minWidth: 0 }}>
                    <p style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Overall Distribution
                    </p>
                    <ResponsiveContainer width="100%" height={240}>
                      <PieChart>
                        <Pie
                          data={overallPieData}
                          cx="50%" cy="50%"
                          innerRadius={60} outerRadius={100}
                          paddingAngle={3}
                          dataKey="value"
                          labelLine={false}
                          label={renderPieLabel}
                        >
                          {overallPieData.map((_, i) => (
                            <Cell key={i} fill={PIE_COLORS[i]} />
                          ))}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                        <Legend
                          iconType="circle"
                          iconSize={8}
                          wrapperStyle={{ fontSize: 12, color: '#475569' }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Grouped bar — province-wise */}
                  {provinceBarData.length > 0 && (
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Province-wise Breakdown
                      </p>
                      <ResponsiveContainer width="100%" height={240}>
                        <BarChart data={provinceBarData} margin={{ top: 4, right: 10, left: -10, bottom: 4 }} barSize={16}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                          <XAxis dataKey="province" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                          <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                          <Tooltip content={<CustomTooltip />} cursor={{ fill: '#f1f5f9' }} />
                          <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, color: '#475569' }} />
                          <Bar dataKey="Male"   fill={GENDER_COLORS.male}   radius={[4,4,0,0]} />
                          <Bar dataKey="Female" fill={GENDER_COLORS.female} radius={[4,4,0,0]} />
                          <Bar dataKey="Other"  fill={GENDER_COLORS.other}  radius={[4,4,0,0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* ════════════════════════════════════════
              ETHNICITY SUMMARY
          ════════════════════════════════════════ */}
          <div className="dash-card">
            <SectionHeader
              icon={<BsPeopleFill />}
              title="Ethnicity Summary"
              accent="#a855f7"
              subtitle="Trainee count grouped by ethnicity and gender"
            />

            {ethnicityLoading ? <Shimmer /> : ethnicities.length === 0 ? (
              <p style={{ color: '#94a3b8', fontSize: 13 }}>No ethnicity data available.</p>
            ) : (
              <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>

                {/* Grouped bar — ethnicity */}
                <div style={{ flex: 2, minWidth: 280 }}>
                  <p style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Male / Female / Other by Ethnicity
                  </p>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={ethnicityBarData} margin={{ top: 4, right: 10, left: -10, bottom: 4 }} barSize={18}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="ethnicity" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                      <Tooltip content={<CustomTooltip />} cursor={{ fill: '#faf5ff' }} />
                      <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, color: '#475569' }} />
                      <Bar dataKey="Male"   fill={GENDER_COLORS.male}   radius={[4,4,0,0]} />
                      <Bar dataKey="Female" fill={GENDER_COLORS.female} radius={[4,4,0,0]} />
                      <Bar dataKey="Other"  fill={GENDER_COLORS.other}  radius={[4,4,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Total per ethnicity — horizontal bar */}
                <div style={{ flex: 1, minWidth: 240 }}>
                  <p style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Total per Ethnicity
                  </p>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart
                      data={ethnicityBarData}
                      layout="vertical"
                      margin={{ top: 4, right: 30, left: 10, bottom: 4 }}
                      barSize={18}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                      <YAxis type="category" dataKey="ethnicity" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} width={90} />
                      <Tooltip content={<CustomTooltip />} cursor={{ fill: '#faf5ff' }} />
                      <Bar dataKey="Total" radius={[0,4,4,0]} fill="#a855f7">
                        <LabelList dataKey="Total" position="right" style={{ fontSize: 11, fontWeight: 700, fill: '#7e22ce' }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>

            {/* ════════════════════════════════════════
              TRAINING LIST
          ════════════════════════════════════════ */}
          <div className="dash-card">
            <SectionHeader
              icon={<MdOutlineSchool />}
              title="Training Programs"
              accent="#0ea5e9"
              subtitle="Click on a training to view detailed trainee information"
            />

            {/* search bar */}
            <form onSubmit={handleTrainingSearch} style={{ display: 'flex', gap: 10, marginBottom: 18 }}>
              <div style={{ flex: 1, position: 'relative' }}>
                <FaSearch style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: 12 }} />
                <input
                  type="text"
                  placeholder="Search training by name or code..."
                  value={trainingQuery}
                  onChange={e => setTrainingQuery(e.target.value)}
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    padding: '9px 12px 9px 32px', borderRadius: 9,
                    border: '1.5px solid #e2e8f0', fontSize: 13,
                    background: '#f8fafc', color: '#1e293b', outline: 'none'
                  }}
                  onFocus={e => (e.target.style.borderColor = '#0ea5e9')}
                  onBlur={e  => (e.target.style.borderColor = '#e2e8f0')}
                />
              </div>
              <button type="submit" style={{
                padding: '9px 18px', borderRadius: 9, border: 'none', cursor: 'pointer',
                background: 'linear-gradient(135deg,#0284c7,#0ea5e9)',
                color: '#fff', fontSize: 13, fontWeight: 600,
                boxShadow: '0 4px 12px rgba(14,165,233,0.3)'
              }}>
                <FaSearch size={11} />
              </button>
              {trainingSearch && (
                <button type="button" onClick={handleTrainingReset} style={{
                  padding: '9px 14px', borderRadius: 9, cursor: 'pointer',
                  background: '#fff', color: '#64748b', fontSize: 13, fontWeight: 600,
                  border: '1.5px solid #e2e8f0'
                }}>
                  <FaTimes size={11} />
                </button>
              )}
            </form>

            {/* grid */}
            {trainingListLoading ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))', gap: 12 }}>
                {[1,2,3,4,5,6].map(i => <Shimmer key={i} h={110} />)}
              </div>
            ) : trainingList.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '28px 0', color: '#94a3b8', fontSize: 14 }}>
                <MdOutlineSchool size={38} style={{ marginBottom: 8, opacity: 0.3 }} />
                <p style={{ margin: 0 }}>No trainings found.</p>
              </div>
            ) : (
              <div className="training-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(270px,1fr))', gap: 12 }}>
                {trainingList.map((t: any) => (
                  <div key={t.id} className="training-card" onClick={() => openModal(t.id)}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                      <span style={{
                        fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 6,
                        background: '#e0f2fe', color: '#0369a1', letterSpacing: 0.3
                      }}>
                        {t.training_code}
                      </span>
                      {/* {t.is_active && (
                        <span style={{
                          fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 20,
                          background: '#dcfce7', color: '#16a34a'
                        }}>Active</span>
                      )} */}
                    </div>
                    <p style={{ margin: '0 0 10px', fontWeight: 700, fontSize: 13, color: '#1e293b', lineHeight: 1.4 }}>
                      {t.training_name}
                    </p>
                    <div style={{ display: 'flex', gap: 12, fontSize: 12, color: '#64748b' }}>
                      <span><strong style={{ color: '#0ea5e9' }}>{t.total_trainees}</strong> Trainees</span>
                      <span><strong style={{ color: '#6366f1' }}>{t.total_routines}</strong> Batches</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* pagination */}
            {!trainingListLoading && totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 18, flexWrap: 'wrap', gap: 10 }}>
                <span style={{ fontSize: 12, color: '#64748b' }}>
                  Page <strong>{trainingPage}</strong> of <strong>{totalPages}</strong>
                  &nbsp;· <strong>{totalTrainings}</strong> total trainings
                </span>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button
                    disabled={trainingPage <= 1}
                    onClick={() => setTrainingPage(p => p - 1)}
                    style={{
                      padding: '6px 14px', borderRadius: 8, border: '1.5px solid #e2e8f0',
                      background: trainingPage <= 1 ? '#f8fafc' : '#fff',
                      color: trainingPage <= 1 ? '#cbd5e1' : '#374151',
                      cursor: trainingPage <= 1 ? 'not-allowed' : 'pointer',
                      fontSize: 13, fontWeight: 600
                    }}
                  >← Prev</button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const page = Math.max(1, Math.min(trainingPage - 2, totalPages - 4)) + i;
                    return (
                      <button key={page} onClick={() => setTrainingPage(page)} style={{
                        padding: '6px 12px', borderRadius: 8, border: '1.5px solid',
                        borderColor: page === trainingPage ? '#0ea5e9' : '#e2e8f0',
                        background: page === trainingPage ? '#0ea5e9' : '#fff',
                        color: page === trainingPage ? '#fff' : '#374151',
                        cursor: 'pointer', fontSize: 13, fontWeight: 600
                      }}>{page}</button>
                    );
                  })}
                  <button
                    disabled={trainingPage >= totalPages}
                    onClick={() => setTrainingPage(p => p + 1)}
                    style={{
                      padding: '6px 14px', borderRadius: 8, border: '1.5px solid #e2e8f0',
                      background: trainingPage >= totalPages ? '#f8fafc' : '#fff',
                      color: trainingPage >= totalPages ? '#cbd5e1' : '#374151',
                      cursor: trainingPage >= totalPages ? 'not-allowed' : 'pointer',
                      fontSize: 13, fontWeight: 600
                    }}
                  >Next →</button>
                </div>
              </div>
            )}
          </div>

          {/* ════════════════════════════════════════
              TRAINING HISTORY
          ════════════════════════════════════════ */}
          <div className="dash-card">
            <SectionHeader
              icon={<HiOutlineAcademicCap />}
              title="Trainee Training History"
              accent="#10b981"
              subtitle="Search and explore individual trainee records"
            />

            {/* Search Form */}
            <div style={{
              background: '#f8fafc', borderRadius: 14, padding: '18px 16px',
              marginBottom: 20, border: '1px solid #e2e8f0'
            }}>
              <form onSubmit={handleSearch}>
                <div className="search-grid" style={{
                  display: 'grid', gridTemplateColumns: 'repeat(4,1fr)',
                  gap: '12px 14px', marginBottom: 14
                }}>
                  {[
                    { name: 'name',          label: 'Name',           placeholder: 'Search by name',      type: 'text'  },
                    { name: 'namc_or_nhpc',  label: 'NAMC / NHPC No.',placeholder: 'Enter NAMC or NHPC',  type: 'text'  },
                    { name: 'mobile_number', label: 'Mobile Number',  placeholder: 'Enter mobile number', type: 'text'  },
                    { name: 'email',         label: 'Email Address',  placeholder: 'Enter email',         type: 'email' }
                  ].map(f => (
                    <div key={f.name}>
                      <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#475569', marginBottom: 5, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                        {f.label}
                      </label>
                      <input
                        type={f.type} name={f.name}
                        placeholder={f.placeholder}
                        value={(filters as any)[f.name] || ''}
                        onChange={handleInputChange}
                        style={{
                          width: '100%', boxSizing: 'border-box', padding: '8px 12px',
                          borderRadius: 8, fontSize: 13, border: '1.5px solid #e2e8f0',
                          background: '#fff', color: '#1e293b', outline: 'none',
                          transition: 'border-color 0.2s'
                        }}
                        onFocus={e => (e.target.style.borderColor = '#3b82f6')}
                        onBlur={e  => (e.target.style.borderColor = '#e2e8f0')}
                      />
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button type="submit" style={{
                    display: 'inline-flex', alignItems: 'center', gap: 7,
                    padding: '9px 22px', borderRadius: 9, border: 'none', cursor: 'pointer',
                    background: 'linear-gradient(135deg,#2563eb,#3b82f6)',
                    color: '#fff', fontSize: 13, fontWeight: 600,
                    boxShadow: '0 4px 12px rgba(37,99,235,0.3)'
                  }}>
                    <FaSearch size={11} /> Search
                  </button>
                  <button type="button" onClick={handleReset} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 7,
                    padding: '9px 20px', borderRadius: 9, cursor: 'pointer',
                    background: '#fff', color: '#64748b', fontSize: 13, fontWeight: 600,
                    border: '1.5px solid #e2e8f0'
                  }}>
                    <FaRedo size={10} /> Reset
                  </button>
                </div>
              </form>
            </div>

            {/* Results */}
            {!searchEnabled ? (
              <div style={{ textAlign: 'center', padding: '32px 16px', color: '#94a3b8' }}>
                <BsPeopleFill size={38} style={{ marginBottom: 10, opacity: 0.3 }} />
                <p style={{ margin: 0, fontSize: 14 }}>Enter search criteria above to find trainee records.</p>
              </div>
            ) : historyLoading ? (
              <div style={{ textAlign: 'center', padding: '32px 16px' }}>
                <div className="spinner-border text-primary" style={{ width: 28, height: 28 }} role="status" />
                <p style={{ margin: '10px 0 0', color: '#64748b', fontSize: 13 }}>Searching...</p>
              </div>
            ) : traineeList.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '32px 16px', color: '#94a3b8' }}>
                <FaUsers size={36} style={{ marginBottom: 10, opacity: 0.28 }} />
                <p style={{ margin: 0, fontSize: 14 }}>No trainees found for the given criteria.</p>
              </div>
            ) : (
              <div style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: 'linear-gradient(90deg,#1e40af,#3b82f6)', color: '#fff' }}>
                        {['#','Name','NAMC/NHPC','Province','District','Email','Trainings',''].map((h, i) => (
                          <th key={i} className={i >= 3 && i <= 5 ? 'td-hide-sm' : ''} style={{
                            padding: '12px 14px', fontWeight: 600, fontSize: 11,
                            letterSpacing: 0.5, textTransform: 'uppercase',
                            textAlign: i >= 7 ? 'center' : 'left', whiteSpace: 'nowrap'
                          }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {traineeList.map((trainee, idx) => (
                        <React.Fragment key={trainee.trainee_id}>
                          <tr className="tr-hover" style={{
                            background: idx % 2 === 0 ? '#fff' : '#f8fafc',
                            borderBottom: '1px solid #f1f5f9', transition: 'background 0.15s'
                          }}>
                            <td style={{ padding: '11px 14px', color: '#94a3b8', fontWeight: 600 }}>{idx + 1}</td>
                            <td style={{ padding: '11px 14px', fontWeight: 700, color: '#1e293b', whiteSpace: 'nowrap' }}>{trainee.trainee_name || '—'}</td>
                            <td style={{ padding: '11px 14px', color: '#475569' }}>{trainee.namc_or_nhpc || '—'}</td>
                            <td className="td-hide-sm" style={{ padding: '11px 14px', color: '#475569' }}>{trainee.province || '—'}</td>
                            <td className="td-hide-sm" style={{ padding: '11px 14px', color: '#475569' }}>{trainee.district || '—'}</td>
                            {/* <td className="td-hide-sm" style={{ padding: '11px 14px', color: '#475569' }}>{trainee.mobile_number || '—'}</td> */}
                            <td style={{ padding: '11px 14px', color: '#475569', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {trainee.email || '—'}
                            </td>
                            <td style={{ padding: '11px 14px', textAlign: 'center' }}>
                              <span style={{
                                display: 'inline-block', minWidth: 28, padding: '2px 10px',
                                borderRadius: 20, fontSize: 12, fontWeight: 700,
                                background: 'linear-gradient(135deg,#2563eb,#60a5fa)',
                                color: '#fff', boxShadow: '0 2px 6px rgba(37,99,235,0.3)'
                              }}>{trainee.total_trainings}</span>
                            </td>
                            <td style={{ padding: '11px 14px', textAlign: 'center' }}>
                              <button
                                onClick={() => setExpandedTrainee(expandedTrainee === trainee.trainee_id ? null : trainee.trainee_id)}
                                style={{
                                  display: 'inline-flex', alignItems: 'center', gap: 5,
                                  padding: '5px 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
                                  background: expandedTrainee === trainee.trainee_id ? '#1e40af' : '#eff6ff',
                                  color: expandedTrainee === trainee.trainee_id ? '#fff' : '#3b82f6',
                                  fontSize: 11, fontWeight: 700, transition: 'all 0.2s'
                                }}
                              >
                                {expandedTrainee === trainee.trainee_id
                                  ? <><FaChevronUp size={9} />Hide</>
                                  : <><FaChevronDown size={9} />View</>}
                              </button>
                            </td>
                          </tr>

                          {expandedTrainee === trainee.trainee_id && (
                            <tr className="expand-row">
                              <td colSpan={9} style={{ padding: 0, background: '#f0f7ff', borderBottom: '2px solid #bfdbfe' }}>
                                <div style={{ padding: '16px 20px' }}>
                                  <p style={{ margin: '0 0 10px', fontWeight: 700, fontSize: 13, color: '#1e40af', display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <HiOutlineAcademicCap size={15} />
                                    Training records — {trainee.trainee_name}
                                  </p>
                                  {trainee.trainings?.length > 0 ? (
                                    <div style={{ borderRadius: 10, overflow: 'hidden', border: '1px solid #bfdbfe' }}>
                                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                        <thead>
                                          <tr style={{ background: '#dbeafe' }}>
                                            {['#','Training Name','Batch','Code','Start Date','End Date'].map((h, i) => (
                                              <th key={i} style={{
                                                padding: '9px 12px', textAlign: 'left',
                                                fontWeight: 700, color: '#1e40af',
                                                fontSize: 11, textTransform: 'uppercase',
                                                letterSpacing: 0.4, borderBottom: '1px solid #bfdbfe'
                                              }}>{h}</th>
                                            ))}
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {trainee.trainings.map((t, tIdx) => (
                                            <tr key={tIdx} style={{
                                              background: tIdx % 2 === 0 ? '#fff' : '#f0f9ff',
                                              borderBottom: '1px solid #e0f2fe'
                                            }}>
                                              <td style={{ padding: '8px 12px', color: '#94a3b8', fontWeight: 600 }}>{tIdx + 1}</td>
                                              <td style={{ padding: '8px 12px', color: '#1e293b', fontWeight: 600 }}>{t.training_name}</td>
                                              <td style={{ padding: '8px 12px', color: '#475569' }}>{t.batch_name}</td>
                                              <td style={{ padding: '8px 12px' }}>
                                                <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 11, background: '#e0e7ff', color: '#4338ca', fontWeight: 700 }}>
                                                  {t.training_code}
                                                </span>
                                              </td>
                                              <td style={{ padding: '8px 12px', color: '#475569', whiteSpace: 'nowrap' }}>{formatDate(t.start_date)}</td>
                                              <td style={{ padding: '8px 12px', color: '#475569', whiteSpace: 'nowrap' }}>{formatDate(t.end_date)}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  ) : (
                                    <p style={{ margin: 0, color: '#94a3b8', fontSize: 13 }}>No training records found.</p>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{
                  padding: '10px 16px', background: '#f8fafc', borderTop: '1px solid #e2e8f0',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  fontSize: 12, color: '#64748b'
                }}>
                  <span>Showing <strong>{traineeList.length}</strong> result{traineeList.length !== 1 ? 's' : ''}</span>
                  <span style={{ fontSize: 11 }}>Page {filters.pageNo} · {filters.pageSize} per page</span>
                </div>
              </div>
            )}
          </div>

        </div>
      </div>

      {/* ════════════════════════════════════════
          TRAINING DETAIL MODAL
      ════════════════════════════════════════ */}
      {modalOpen && (
        <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) closeModal(); }}>
          <div className="modal-box" ref={modalRef}>

            {/* modal header */}
            <div style={{
              background: 'linear-gradient(135deg,#0284c7,#0ea5e9)',
              padding: '20px 24px', borderRadius: '20px 20px 0 0',
              display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'
            }}>
              <div>
                <p style={{ margin: '0 0 3px', fontSize: 11, color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}>
                  Training Detail
                </p>
                <h4 style={{ margin: 0, color: '#fff', fontSize: 17, fontWeight: 800 }}>
                  {detailLoading ? 'Loading…' : trainingDetail?.training_name || 'Training Information'}
                </h4>
                {trainingDetail?.training_code && (
                  <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)', fontWeight: 600 }}>
                    Code: {trainingDetail.training_code}
                  </span>
                )}
              </div>
              <button onClick={closeModal} style={{
                background: 'rgba(255,255,255,0.15)', border: 'none', cursor: 'pointer',
                borderRadius: 8, padding: '6px 9px', color: '#fff', fontSize: 14, lineHeight: 1
              }}>
                <FaTimes />
              </button>
            </div>

            {detailLoading ? (
              <div style={{ padding: 32, textAlign: 'center' }}>
                <div className="spinner-border text-primary" style={{ width: 28, height: 28 }} role="status" />
                <p style={{ margin: '10px 0 0', color: '#64748b', fontSize: 13 }}>Loading details…</p>
              </div>
            ) : trainingDetail ? (
              <div style={{ padding: '20px 24px' }}>

                {/* stat pills */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 22 }}>
                  <StatPill label="Total Trainees" value={trainingDetail.total_trainees}           color="#6366f1" />
                  <StatPill label="Male"            value={trainingDetail.gender_summary?.male}    color={GENDER_COLORS.male} />
                  <StatPill label="Female"          value={trainingDetail.gender_summary?.female}  color={GENDER_COLORS.female} />
                  {trainingDetail.gender_summary?.other > 0 && (
                    <StatPill label="Other"         value={trainingDetail.gender_summary?.other}   color={GENDER_COLORS.other} />
                  )}
                </div>

                {/* province chart */}
                {modalProvinceBar.length > 0 && (
                  <div style={{ marginBottom: 22 }}>
                    <p style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                      Province-wise Distribution
                    </p>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={modalProvinceBar} margin={{ top: 4, right: 10, left: -10, bottom: 4 }} barSize={14}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="province" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                        <Tooltip content={<CustomTooltip />} cursor={{ fill: '#f0f9ff' }} />
                        <Legend iconType="circle" iconSize={7} wrapperStyle={{ fontSize: 11 }} />
                        <Bar dataKey="Male"   fill={GENDER_COLORS.male}   radius={[4,4,0,0]} />
                        <Bar dataKey="Female" fill={GENDER_COLORS.female} radius={[4,4,0,0]} />
                        <Bar dataKey="Other"  fill={GENDER_COLORS.other}  radius={[4,4,0,0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* trainee list */}
                {trainingDetail.trainees?.length > 0 && (
                  <div>
                    <p style={{ margin: '0 0 10px', fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                      Enrolled Trainees ({trainingDetail.trainees.length})
                    </p>
                    <div style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid #e2e8f0', maxHeight: 260, overflowY: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                          <tr style={{ background: '#f1f5f9' }}>
                            <th style={{ padding: '9px 14px', textAlign: 'left', fontWeight: 700, color: '#475569', fontSize: 11, textTransform: 'uppercase' }}>#</th>
                            <th style={{ padding: '9px 14px', textAlign: 'left', fontWeight: 700, color: '#475569', fontSize: 11, textTransform: 'uppercase' }}>Trainee Name</th>
                            <th style={{ padding: '9px 14px', textAlign: 'left', fontWeight: 700, color: '#475569', fontSize: 11, textTransform: 'uppercase' }}>Batch</th>
                          </tr>
                        </thead>
                        <tbody>
                          {trainingDetail.trainees.map((tr: any, i: number) => (
                            <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
                              <td style={{ padding: '9px 14px', color: '#94a3b8', fontWeight: 600 }}>{i + 1}</td>
                              <td style={{ padding: '9px 14px', fontWeight: 600, color: '#1e293b' }}>{tr.trainee_name}</td>
                              <td style={{ padding: '9px 14px' }}>
                                <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 11, background: '#e0f2fe', color: '#0369a1', fontWeight: 700 }}>
                                  {tr.batch_name}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>
                No data available for this training.
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default TraineeDashboard;
